<script>
import { mapStores } from 'pinia'

import { useCartStore } from '@/cart/stores/cart'
import CartFooter from '@/cart/components/CartFooter.vue'
import api from '@/cartapi.js'

export default {
  name: 'CartApp',

  components: {
    CartFooter,
  },

  data() {
    return {
      showFooterPolicyModal: false,
      footerPolicyTitle: '',
      footerPolicyContent: '',
    }
  },

  computed: {
    ...mapStores(useCartStore),

    vendorSlug() {
      const host = window.location.hostname || ''
      const parts = host.split('.')
      return parts[0] || ''
    },

    vendor() {
      return this.cartStore.vendor
    },

    currentEvent() {
      return this.cartStore.selectedEvent
    },

    commSettings() {
      return this.cartStore.commSettings
    },

    isPreviewMode() {
      return this.cartStore.isPreviewMode
    },

    liveSiteUrl() {
      if (!this.isPreviewMode) return null
      const url = new URL(window.location.href)
      url.searchParams.delete('preview')
      return url.toString()
    },
  },

  async created() {
    if (this.cartStore.vendor || !this.vendorSlug) {
      return
    }

    try {
      const vendor = await api.vendors.get(this.vendorSlug)
      this.cartStore.setVendor(vendor)
    } catch (err) {
      this.cartStore.setVendor(null)
    }
  },

  methods: {
    handleViewPolicy({ title, content }) {
      this.footerPolicyTitle = title
      this.footerPolicyContent = content
      this.showFooterPolicyModal = true
    },
  },
}
</script>

<template>
  <div class="cart-app">
    <!-- Preview Mode Banner (persists through entire flow) -->
    <div v-if="isPreviewMode" class="preview-banner">
      <div class="container">
        <div class="preview-banner__content">
          <b-icon icon="eye-outline" size="is-small" />
          <span><strong>Preview Mode</strong> — You're viewing as an operator. Purchases are disabled.</span>
          <a v-if="liveSiteUrl" :href="liveSiteUrl" target="_blank" class="preview-banner__link">
            View live site →
          </a>
        </div>
      </div>
    </div>

    <div class="cart-content">
      <router-view />
    </div>
    
    <CartFooter 
      v-if="vendor"
      :vendor="vendor"
      :settings="commSettings"
      @view-policy="handleViewPolicy"
    />

    <!-- Footer Policy Modal -->
    <b-modal v-model="showFooterPolicyModal" has-modal-card scroll="keep">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ footerPolicyTitle }}</p>
          <button class="delete" aria-label="close" @click="showFooterPolicyModal = false" />
        </header>
        <section class="modal-card-body">
          <div class="content" style="white-space: pre-wrap">{{ footerPolicyContent }}</div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showFooterPolicyModal = false">Close</b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss">
@import '@mdi/font/css/materialdesignicons.css';
</style>

<style scoped>
.cart-app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.cart-content {
  flex: 1;
}

/* Preview Mode Banner */
.preview-banner {
  padding: 0.75rem 0;
  background: linear-gradient(135deg, #fff7e6 0%, #ffe4b5 100%);
  border-bottom: 1px solid #f0c36d;
}

.preview-banner__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #856404;
  font-size: 0.9rem;
}

.preview-banner__link {
  color: #664d03;
  font-weight: 600;
  text-decoration: underline;
  margin-left: 0.5rem;
  white-space: nowrap;
}

.preview-banner__link:hover {
  color: #332701;
}
</style>